import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import type { ProfileTab } from '@/types/profile';
import type { User as UserType } from '@/types/domain/user';
import { getUserInitials } from '@/lib/user';
import { Bell, CalendarDays, Camera, Heart, Loader2, LogOut, Search, UserRound } from 'lucide-react';

const items: Array<{ id: ProfileTab; label: string; description: string; icon: typeof UserRound }> = [
  { id: 'details', label: 'Dados pessoais', description: 'Cadastro e contato', icon: UserRound },
  { id: 'bookings', label: 'Agenda e filas', description: 'Seus atendimentos', icon: CalendarDays },
  { id: 'favorites', label: 'Favoritos', description: 'Locais que você salvou', icon: Heart },
  { id: 'preferences', label: 'Preferências', description: 'Notificações e conta', icon: Bell },
];

interface ProfileSidebarProps {
  activeTab: ProfileTab;
  counters: { bookings: number; favorites: number; walkIns: number };
  displayName: string;
  contactLabel: string;
  isLoggingOut: boolean;
  isUploadingPhoto: boolean;
  onChangeTab: (tab: ProfileTab) => void;
  onFindPlaces: () => void;
  onLogout: () => void;
  onOpenPhotoPicker: () => void;
  photoInput: React.ReactNode;
  user?: UserType | null;
}

export function ProfileSidebar({
  activeTab,
  contactLabel,
  counters,
  displayName,
  isLoggingOut,
  isUploadingPhoto,
  onChangeTab,
  onFindPlaces,
  onLogout,
  onOpenPhotoPicker,
  photoInput,
  user,
}: ProfileSidebarProps) {
  const badgeFor = (tab: ProfileTab) => {
    if (tab === 'bookings') return counters.bookings + counters.walkIns;
    if (tab === 'favorites') return counters.favorites;
    return 0;
  };

  return (
    <aside className="lg:sticky lg:top-20 lg:self-start">
      <div className="border-border bg-card overflow-hidden rounded-3xl border shadow-sm">
        <div className="from-primary/15 via-primary/5 to-card bg-gradient-to-br p-5">
          <div className="flex items-center gap-4 lg:flex-col lg:text-center">
            <div className="relative shrink-0">
              <button
                type="button"
                onClick={onOpenPhotoPicker}
                disabled={isUploadingPhoto}
                aria-label="Alterar foto de perfil"
                className="group focus-visible:ring-ring relative block rounded-full focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none disabled:opacity-70"
              >
                <Avatar className="border-card h-20 w-20 border-4 shadow-md lg:h-24 lg:w-24">
                  <AvatarImage src={user?.photo ?? undefined} alt={displayName} className="object-cover" />
                  <AvatarFallback className="bg-primary text-primary-foreground text-xl font-bold">
                    {getUserInitials(user)}
                  </AvatarFallback>
                </Avatar>
                <span className="bg-primary text-primary-foreground absolute right-0 bottom-0 flex h-7 w-7 items-center justify-center rounded-full shadow-sm">
                  {isUploadingPhoto ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Camera className="h-3.5 w-3.5" />
                  )}
                </span>
              </button>
              {photoInput}
            </div>
            <div className="min-w-0">
              <p className="text-foreground truncate text-lg font-bold">{displayName}</p>
              <p className="text-muted-foreground mt-0.5 truncate text-sm">{contactLabel}</p>
              <span className="bg-success/10 text-success mt-2 inline-flex rounded-full px-2.5 py-1 text-xs font-semibold">
                Conta ativa
              </span>
            </div>
          </div>
        </div>

        <nav aria-label="Seções do perfil" className="grid grid-cols-4 gap-1 border-t p-2 lg:block lg:space-y-1 lg:p-3">
          {items.map((item) => {
            const Icon = item.icon;
            const active = item.id === activeTab;
            const badge = badgeFor(item.id);

            return (
              <button
                key={item.id}
                type="button"
                onClick={() => onChangeTab(item.id)}
                aria-label={item.label}
                aria-current={active ? 'page' : undefined}
                className={`relative flex min-w-0 flex-col items-center gap-1 rounded-2xl px-2 py-2.5 text-center transition-colors lg:flex-row lg:gap-3 lg:px-3 lg:py-3 lg:text-left ${
                  active ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                }`}
              >
                <span
                  className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-xl ${active ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}
                >
                  <Icon className="h-4 w-4" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[11px] font-semibold sm:text-xs lg:text-sm">{item.label}</span>
                  <span className="hidden text-xs opacity-70 lg:block">{item.description}</span>
                </span>
                {badge > 0 ? (
                  <span className="bg-primary text-primary-foreground absolute top-1 right-1 min-w-5 rounded-full px-1.5 text-center text-[10px] leading-5 lg:static">
                    {badge}
                  </span>
                ) : null}
              </button>
            );
          })}
        </nav>

        <div className="border-border hidden space-y-1 border-t p-3 lg:block">
          <button
            type="button"
            onClick={onFindPlaces}
            className="text-muted-foreground hover:bg-muted hover:text-foreground flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors"
          >
            <Search className="h-4 w-4" /> Encontrar locais
          </button>
          <button
            type="button"
            onClick={onLogout}
            disabled={isLoggingOut}
            className="text-destructive hover:bg-destructive/5 flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors disabled:opacity-50"
          >
            {isLoggingOut ? <Loader2 className="h-4 w-4 animate-spin" /> : <LogOut className="h-4 w-4" />}
            {isLoggingOut ? 'Saindo…' : 'Sair da conta'}
          </button>
        </div>
      </div>
    </aside>
  );
}
